import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MeetingProvider, useMeeting, useParticipant, VideoPlayer } from '@videosdk.live/react-sdk';
import { useSelector } from 'react-redux';
import { Participant } from '@videosdk.live/react-sdk/dist/types/participant';
import { Globals } from 'utils';
import { RootState } from 'reducers';
import { WhiteImage } from 'assets/img';

const INIT_VIDEO = true;
const INIT_MIC = false;

interface Props {
  meeting_id: string;
  token: string;
  onClose: () => void;
}

const VideoSDKRoom = ({ meeting_id, token, onClose }: Props) => {
  const user = useSelector((state: RootState) => state.user);
  const [availability, setAvailability] = useState({ video: false, audio: false });
  const [meetingId, setMeetingId] = useState<string | null>(meeting_id);

  const checkMedia = async () => {
    const devices = await navigator.mediaDevices?.enumerateDevices() || [];

    let hasCamera = devices.some(d => d.kind === 'videoinput');
    let hasMic = devices.some(d => d.kind === 'audioinput');

    setAvailability({ video: hasCamera, audio: hasMic });
  }

  const onMeetingLeft = () => {
    setMeetingId(null);
    setTimeout(() => {
      onClose();
    }, 500);
  }

  useEffect(() => {
    checkMedia();
  }, []);

  return (
    <>
      {!availability.video && (
        <p className="not-found">No hay cámara disponible</p>
      )}
      {!availability.audio && (
        <p className="not-found">No hay micrófono disponible</p>
      )}

      <div className="video-sdk-container">
        {(!!meetingId && !!token) && (
          <MeetingProvider
            config={{
              meetingId,
              micEnabled: INIT_MIC,
              webcamEnabled: INIT_VIDEO,
              name: user?.name || 'Usuario de prueba',
              metaData: { user_id: user?.id },
              defaultCamera: 'front',
              maxResolution: 'hd',
              debugMode: process.env.NODE_ENV === 'development',
            }}
            token={token || ''}
          >
            <MeetingView meetingId={meeting_id} onMeetingLeft={onMeetingLeft} />
          </MeetingProvider>
        )}
      </div>
    </>
  );
}

function MeetingView({ onMeetingLeft }: any) {
  const user = useSelector((state: RootState) => state.user);
  const [isJoined, setIsJoined] = useState(false);
  const [cameras, setCameras] = useState<any[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<number>(0);

  const { join, leave, participants, muteMic, unmuteMic, enableWebcam, disableWebcam, changeWebcam, getWebcams } = useMeeting({
    onMeetingJoined: () => {
      setIsJoined(true);
      handleGetWebcams();
    },
    onMeetingLeft: () => {
      setIsJoined(false);
      onMeetingLeft();
      leave();
    },
  });

  const handleGetWebcams = async () => {
    const webcams = await getWebcams();
    console.log(webcams)
    setCameras(webcams || []);
  };

  const handleChangeCamera = () => {
    if (!cameras.length) return;
    let index = selectedCamera + 1;
    if (index === cameras.length) index = 0;
    changeWebcam(cameras[index].deviceId);
    setSelectedCamera(index);
  };

  const { mySelf, participant } = useMemo(() => {
    const users: Array<Participant> = [];
    participants.forEach(p => users.push(p));

    const _self = users.find(p => (p.metaData as any)?.user_id === user?.id);
    const _other = users.find(p => (p.metaData as any)?.user_id !== user?.id);

    return {
      mySelf: _self?.id || null,
      participant: _other?.id || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants.size, isJoined]);

  useEffect(() => {
    return () => {
      leave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="video-sdk-meeting-view">
        <div className="video-sdk-participant-cam-wrapper">
          {participant
            ? <ParticipantView participantId={participant} />
            : <img src={WhiteImage} className="video-sdk-participant-cam-placeholder" alt="Placeholder" />
          }
        </div>

        {!!mySelf && <MySelfView participantId={mySelf} />}
      </div>

      <Controls
        isJoined={isJoined}
        muteMic={muteMic}
        unmuteMic={unmuteMic}
        enableWebcam={enableWebcam}
        disableWebcam={disableWebcam}
        changeWebcam={handleChangeCamera}
        join={join}
        leave={leave}
      />
    </>
  );
}

function MySelfView({ participantId }: any) {
  const { webcamStream, webcamOn } = useParticipant(participantId);

  return (
    <div className="video-sdk-myself-cam-wrapper">
      {(webcamOn && webcamStream) ? (
        <VideoPlayer
          participantId={participantId}
          type="video"
          className="video-sdk-self-cam"
          classNameVideo="video-sdk-self-cam-video"
          containerStyle={{
            height: "160px",
            width: "120px",
            borderRadius: "12px",
            overflow: "hidden"
          }}
        />
      ) : (
        <img src={WhiteImage} className="video-sdk-myself-cam-placeholder" alt="Placeholder" />
      )}
    </div>
  )
}

function ParticipantView({ participantId }: any) {
  const micRef = useRef<HTMLAudioElement>(null);
  const { micStream, webcamOn, micOn, isLocal, displayName, webcamStream } = useParticipant(participantId);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);

        micRef.current.srcObject = mediaStream;
        micRef.current?.play()
          .catch((error) =>
            console.error("videoElem.current.play() failed", error)
          );
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  return (
    <>
      <audio ref={micRef} autoPlay muted={isLocal} />
      {(webcamOn && webcamStream) && (
        <VideoPlayer
          participantId={participantId}
          type="video"
          className="video-sdk-participant-cam"
          classNameVideo="video-sdk-participant-cam-video"
          containerStyle={{
            height: "100%",
            width: "100%",
          }}
        />
      )}

      {!!displayName && (
        <span className="video-sdk-display-name">{displayName}</span>
      )}
    </>
  )
}

const Controls = ({ isJoined, muteMic, unmuteMic, enableWebcam, disableWebcam, changeWebcam, join, leave }: any) => {
  const [isJoining, setIsJoining] = useState(false);
  const [vidEnabled, setVidEnabled] = useState(INIT_VIDEO);
  const [micEnabled, setMicEnabled] = useState(INIT_MIC);

  const onJoin = () => {
    if (isJoining) return;
    setIsJoining(true);
    Globals.setLoading();
    join();
  }

  const onToggleVid = () => {
    vidEnabled ? disableWebcam() : enableWebcam();
    setVidEnabled(!vidEnabled);
  }

  const onToggleMic = () => {
    micEnabled ? muteMic() : unmuteMic();
    setMicEnabled(!micEnabled);
  }

  useEffect(() => {
    setIsJoining(false);
    Globals.quitLoading();
  }, [isJoined]);

  return (
    <div className="video-sdk-controls">
      {!isJoined && (
        <>
          {!isJoining && (
            <button className="video-sdk-btn-join" onClick={onJoin}>
              Unirse
            </button>
          )}
        </>
      )}
      {isJoined && (
        <button className="video-sdk-btn" onClick={changeWebcam}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2" /><path d="M11.245 15.904a3 3 0 0 0 3.755 -2.904m-2.25 -2.905a3 3 0 0 0 -3.75 2.905" /><path d="M14 13h2v2" /><path d="M10 13h-2v-2" /></svg>
        </button>
      )}
      <button className="video-sdk-btn" onClick={onToggleVid}>
        {vidEnabled ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 10l4.553 -2.276a1 1 0 0 1 1.447 .894v6.764a1 1 0 0 1 -1.447 .894l-4.553 -2.276v-4" /><path d="M3 8a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2l0 -8" /></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 3l18 18" /><path d="M15 11v-1l4.553 -2.276a1 1 0 0 1 1.447 .894v6.764a1 1 0 0 1 -.675 .946" /><path d="M10 6h3a2 2 0 0 1 2 2v3m0 4v1a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-8a2 2 0 0 1 2 -2h1" /></svg>
        )}
      </button>
      <button className="video-sdk-btn" onClick={onToggleMic}>
        {micEnabled ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9 5a3 3 0 0 1 3 -3a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3a3 3 0 0 1 -3 -3l0 -5" /><path d="M5 10a7 7 0 0 0 14 0" /><path d="M8 21l8 0" /><path d="M12 17l0 4" /></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 3l18 18" /><path d="M9 5a3 3 0 0 1 6 0v5a3 3 0 0 1 -.13 .874m-2 2a3 3 0 0 1 -3.87 -2.872v-1" /><path d="M5 10a7 7 0 0 0 10.846 5.85m2 -2a6.967 6.967 0 0 0 1.152 -3.85" /><path d="M8 21l8 0" /><path d="M12 17l0 4" /></svg>
        )}
      </button>
      {isJoined && (
        <button className="video-sdk-btn-end" onClick={leave}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" /></svg>
        </button>
      )}
    </div>
  )
}

export default VideoSDKRoom;